import { useDidMount } from 'hooks';
import {
  type RefObject,
  useRef,
  useState,
  DOMAttributes,
  MutableRefObject,
  useCallback
} from 'react';

type DragEvent = MouseEvent & TouchEvent;
const getX = (e: DragEvent) => {
  return e.targetTouches && e.targetTouches.length >= 1 ? e.targetTouches[0].clientX : e.clientX;
};

type DragContext = {
  canDrag: boolean;
  x: number;
  start: number;
  threshold: number;
  delta: number;
  didMove: boolean;
};
type Props = {
  thresholdPercent: number;
  closeToast: () => void;
};

const bindDragEvents = (
  drag: MutableRefObject<DragContext>,
  onDragEnd: (e: unknown) => void,
  onDragMove: (e: unknown) => void
) => {
  drag.current.didMove = false;
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);

  document.addEventListener('touchmove', onDragMove);
  document.addEventListener('touchend', onDragEnd);
};

const unbindDragEvents = (onDragEnd: (e: unknown) => void, onDragMove: (e: unknown) => void) => {
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);

  document.removeEventListener('touchmove', onDragMove);
  document.removeEventListener('touchend', onDragEnd);
};

export const useToast = (
  { closeToast, thresholdPercent }: Props,
  ref: RefObject<HTMLElement>,
  wrapperRef: RefObject<HTMLElement>
) => {
  const [isRunning, setRunning] = useState(false);
  const drag = useRef<DragContext>({
    canDrag: false,
    x: 0,
    start: 0,
    threshold: 0,
    delta: 0,
    didMove: true
  });
  const pauseToast = useCallback(() => {
    setRunning(false);
  }, []);

  const playToast = useCallback(() => {
    setRunning(true);
  }, []);

  const onDragMove = (e: DragEvent) => {
    const toast = ref.current;
    if (!drag.current.canDrag || !toast) {
      return;
    }

    drag.current.didMove = true;
    drag.current.x = getX(e);
    drag.current.delta = drag.current.x - drag.current.start;

    toast.style.transform = `translateX(${drag.current.delta}px)`;
    toast.style.opacity = `${1 - Math.abs(drag.current.delta / drag.current.threshold)}`;
  };
  const onDragEnd = () => {
    unbindDragEvents(onDragEnd, onDragMove);
    const toast = ref.current;
    const wrapper = wrapperRef.current
    if (!drag.current.canDrag || !drag.current.didMove || !toast || !wrapper) {
      return;
    }

    if (Math.abs(drag.current.delta) > drag.current.threshold) {
      wrapper.style.opacity = '0'
      wrapper.style.display = 'none'
      closeToast();
      return;
    }

    toast.style.transition = 'transform 200ms, opacity 200ms';
    toast.style.transform = `translateX(0px)`;
    toast.style.opacity = '1';
  };

  const onDragStart = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | React.TouchEvent<HTMLElement>
  ) => {
    const toast = ref.current;
    if (!toast) {
      return;
    }

    pauseToast()
    bindDragEvents(drag, onDragEnd, onDragMove);

    toast.style.transition = '';

    drag.current.canDrag = true;
    drag.current.x = getX(e.nativeEvent as DragEvent);
    drag.current.start = drag.current.x;
    drag.current.threshold = toast.offsetWidth * (thresholdPercent / 100);
  };

  const onDragTransitionEnd = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | React.TouchEvent<HTMLElement>
  ) => {
    const wasTouch = e.nativeEvent.type === 'touchend'
    const isOverThreshold = Math.abs(drag.current.delta) > drag.current.threshold;

    setRunning(wasTouch && !isOverThreshold)
  };

  useDidMount(() => {
    if (!document.hasFocus()) {
      pauseToast();
    }
    window.addEventListener('focus', playToast);
    window.addEventListener('blur', pauseToast);

    return () => {
      window.removeEventListener('focus', playToast);
      window.removeEventListener('blur', pauseToast);
    };
  });

  const eventHandlers: DOMAttributes<HTMLElement> = {
    onMouseEnter: pauseToast,
    onMouseLeave: playToast,
    onMouseDown: onDragStart,
    onTouchStart: onDragStart,
    onMouseUp: onDragTransitionEnd,
    onTouchEnd: onDragTransitionEnd
  };
  return {
    isRunning,
    playToast,
    pauseToast,
    eventHandlers
  };
};
